import { Plain, Rich } from '@agendor/shared';

export type HoursMarkerItem = { type: 'hours-marker'; time: number; label: string };
export type EmptyIntervalItem = { type: 'empty-interval'; time: number; usesDayLabel: boolean };
export type EventItem = { type: 'event'; time: number };
export type AgendaItem = HoursMarkerItem | EmptyIntervalItem | EventItem;

type EventInput = { startMinutes: number };

export function buildAgendaItems(intervals: Plain.DayTimeSlice[], events: EventInput[]): AgendaItem[] {
    const items: AgendaItem[] = [];

    // Add events as abstract items
    for (const event of events) {
        items.push({ type: 'event', time: event.startMinutes });
    }

    if (intervals.length === 0) {
        return items;
    }

    const formatTime = (h: number, m: number) => `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

    for (let i = 0; i < intervals.length; i++) {
        const interval = intervals[i];
        const prevInterval = i > 0 ? intervals[i - 1] : null;
        const intervalStartMinutes = new Rich.TimeOfDay(interval.start).minutes;
        const intervalEndMinutes = new Rich.TimeOfDay(interval.end).minutes;

        // Check if this opening follows a previous closing (gap/break)
        if (prevInterval) {
            // Combine into "Fechado" marker
            const closeTime = new Rich.TimeOfDay(prevInterval.end).minutes;
            const openTime = new Rich.TimeOfDay(interval.start).minutes;
            if (closeTime < openTime) {
                items.push({
                    type: 'hours-marker',
                    time: closeTime,
                    label: `${formatTime(prevInterval.end.hour === 24 ? 0 : prevInterval.end.hour, prevInterval.end.minute)} - ${formatTime(interval.start.hour, interval.start.minute)} · Fechado`,
                });
            }
        } else {
            // First interval - add opening marker if not 00:00
            if (interval.start.hour !== 0 || interval.start.minute !== 0) {
                items.push({
                    type: 'hours-marker',
                    time: intervalStartMinutes,
                    label: `${formatTime(interval.start.hour, interval.start.minute)} · Abertura`,
                });
            }
        }

        // Check if there are any events in this interval
        const hasEventsInInterval = events.some((event) => {
            return event.startMinutes >= intervalStartMinutes && event.startMinutes < intervalEndMinutes;
        });

        if (!hasEventsInInterval) {
            const is24h =
                interval.start.hour === 0 &&
                interval.start.minute === 0 &&
                interval.end.hour === 24 &&
                interval.end.minute === 0;
            items.push({
                type: 'empty-interval',
                time: intervalStartMinutes + 1, // +1 to appear after opening marker
                usesDayLabel: is24h,
            });
        }

        // Last interval - add closing marker if not 24:00
        if (i === intervals.length - 1) {
            if (interval.end.hour !== 24 || interval.end.minute !== 0) {
                items.push({
                    type: 'hours-marker',
                    time: intervalEndMinutes,
                    label: `${formatTime(interval.end.hour === 24 ? 0 : interval.end.hour, interval.end.minute)} · Fechamento`,
                });
            }
        }
    }

    items.sort((a, b) => {
        if (a.time !== b.time) return a.time - b.time;
        // Hours markers come first, then events, then empty-interval
        const priority = { 'hours-marker': 0, event: 1, 'empty-interval': 2 };
        return priority[a.type] - priority[b.type];
    });

    return items;
}
